import React, { useState, useEffect } from 'react';
import Navbar from '../components/storefront/Navbar';
import Footer from '../components/storefront/Footer';
import supabase from '../supabase/supabase';
import { useNavigate } from 'react-router-dom';
import { FiLock, FiCheckCircle, FiXCircle, FiArrowLeft } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import LoadingSpinner from '../common/LoadingSpinner';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [expired, setExpired] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const initializeSession = async () => {
      try {
        // Check if URL hash contains recovery tokens from Supabase redirect
        const hash = window.location.hash;
        if (hash && hash.includes('access_token=') && hash.includes('type=recovery')) {
          // Parse hash params and set session explicitly
          const params = new URLSearchParams(hash.replace('#', ''));
          const accessToken = params.get('access_token');
          const refreshToken = params.get('refresh_token');

          if (accessToken && refreshToken) {
            const { error: sessionError } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            });

            if (sessionError) {
              setExpired(true);
              setChecking(false);
              return;
            }
          }

          // Clean the URL
          window.history.replaceState(null, '', window.location.pathname);
        }

        // Now check if we have a valid session
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          setExpired(true);
        }
      } catch (err) {
        setExpired(true);
      }
      setChecking(false);
    };

    initializeSession();
  }, []);

  const handleReset = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match. Please try again.');
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      setSuccess(true);
    } catch (err) {
      if (err.message.includes('expired') || err.message.includes('invalid')) {
        setExpired(true);
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="bg-brand-sand min-h-screen pt-24 font-sans flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center p-6">
          <LoadingSpinner size="lg" text="Verifying reset link..." />
        </div>
        <Footer />
      </div>
    );
  }

  if (expired) {
    return (
      <div className="bg-brand-sand min-h-screen pt-24 font-sans flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center p-6 my-10">
          <div className="bg-white/80 backdrop-blur-xl p-8 md:p-12 rounded-[2rem] border border-white shadow-xl w-full max-w-md text-center">
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiXCircle} className="w-8 h-8 text-red-400" />
            </div>
            <h1 className="text-3xl font-serif text-brand-darkSage mb-3">Link Expired</h1>
            <p className="text-brand-lightText text-sm mb-6 leading-relaxed">
              This password reset link has expired or has already been used. For security, reset links are only valid for <strong>15 minutes</strong>.
            </p>
            <p className="text-xs text-brand-lightText mb-8">
              Please proceed to login and request a new password reset.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="w-full bg-brand-darkSage text-white py-4 rounded-full font-bold uppercase tracking-widest text-xs hover:bg-brand-gold transition-all"
            >
              Go to Login
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (success) {
    return (
      <div className="bg-brand-sand min-h-screen pt-24 font-sans flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center p-6 my-10">
          <div className="bg-white/80 backdrop-blur-xl p-8 md:p-12 rounded-[2rem] border border-white shadow-xl w-full max-w-md text-center">
            <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiCheckCircle} className="w-8 h-8 text-green-500" />
            </div>
            <h1 className="text-3xl font-serif text-brand-darkSage mb-3">Password Updated</h1>
            <p className="text-brand-lightText text-sm mb-8 leading-relaxed">
              Your password has been successfully changed. You can now log in with your new password.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="w-full bg-brand-darkSage text-white py-4 rounded-full font-bold uppercase tracking-widest text-xs hover:bg-brand-gold transition-all"
            >
              Sign In
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-brand-sand min-h-screen pt-24 font-sans flex flex-col">
      <Navbar />
      <div className="flex-1 flex items-center justify-center p-6 my-10">
        <div className="bg-white/80 backdrop-blur-xl p-8 md:p-12 rounded-[2rem] border border-white shadow-xl w-full max-w-md">
          <button
            onClick={() => navigate('/login')}
            className="flex items-center gap-2 text-xs font-bold text-brand-darkSage/70 hover:text-brand-gold transition-colors mb-6"
          >
            <FiArrowLeft size={14} /> Back to Login
          </button>

          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-brand-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiLock} className="w-8 h-8 text-brand-gold" />
            </div>
            <h1 className="text-3xl font-serif text-brand-darkSage mb-2">Set New Password</h1>
            <p className="text-brand-lightText text-sm">Enter your new password below. Make sure it's at least 6 characters.</p>
          </div>

          {error && <div className="bg-red-50 text-red-500 p-3 rounded-lg text-sm mb-6 border border-red-100">{error}</div>}

          <form onSubmit={handleReset} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-brand-darkSage uppercase tracking-wider mb-1">New Password</label>
              <input 
                required 
                type="password" 
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border border-brand-darkSage/20 rounded-xl px-4 py-3 bg-white/50 focus:ring-brand-gold outline-none" 
                placeholder="Enter new password"
                minLength={6}
                autoFocus
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-brand-darkSage uppercase tracking-wider mb-1">Confirm New Password</label>
              <input 
                required 
                type="password" 
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full border border-brand-darkSage/20 rounded-xl px-4 py-3 bg-white/50 focus:ring-brand-gold outline-none" 
                placeholder="Confirm new password"
                minLength={6}
              />
              {confirmPassword && password !== confirmPassword && (
                <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
              )}
            </div>

            <button 
              disabled={loading || !password || !confirmPassword || password !== confirmPassword}
              type="submit" 
              className="w-full bg-brand-darkSage text-white py-4 rounded-full font-bold uppercase tracking-widest text-xs hover:bg-brand-gold transition-all disabled:opacity-50"
            >
              {loading ? 'Updating...' : 'Update Password'}
            </button>
          </form>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ResetPassword;